<!--
	SystemRequirements — the computer half of "will it work in my shop?". A
	macOS / Windows switch swaps one spec sheet (minimum against recommended,
	content/requirements.ts), and a live readout beside it reads what the
	browser can see of the machine it is running on and grades each line.

	The readout is honest about its limits: browsers cap or hide memory, Safari
	masks the GPU, and none of them will say how much disk is free. Anything it
	cannot see is "Not visible", never a guess. Without JS the sheet renders
	for macOS and the readout stays in its waiting state.
-->
<script lang="ts">
	import { SectionHead } from '$lib/components/ui';
	import { platforms, studioMachine, unsupported, type Platform } from '$lib/content/requirements';

	let { index = '05' }: { index?: string } = $props();

	/** `ok`: clears the minimum, and the browser cannot see far enough to say more. */
	type Grade = 'rec' | 'min' | 'ok' | 'below' | 'unknown';
	interface Check {
		label: string;
		value: string;
		grade: Grade;
	}
	interface Probe {
		os: Platform | 'other';
		checks: Check[];
	}

	let platform = $state<Platform>('mac');
	let probe = $state<Probe | null>(null);

	const spec = $derived(platforms.find((p) => p.id === platform) ?? platforms[0]);
	const verdict = $derived.by(() => {
		if (!probe) return null;
		if (probe.os === 'other')
			return {
				grade: 'below' as Grade,
				text: 'This device cannot run PPF Engine. Open this page on your shop’s Mac or PC.'
			};
		const grades = probe.checks.map((c) => c.grade);
		if (grades.includes('below'))
			return { grade: 'below' as Grade, text: 'Something here is under the minimum.' };
		const seen = grades.filter((g) => g === 'rec' || g === 'min');
		if (seen.length && seen.every((g) => g === 'rec'))
			return { grade: 'rec' as Grade, text: 'Everything we can see is at or above recommended.' };
		return { grade: 'min' as Grade, text: 'Clears the minimum on everything we can see.' };
	});

	const gradeText: Record<Grade, string> = {
		rec: 'Recommended',
		min: 'Minimum',
		ok: 'Meets minimum',
		below: 'Below',
		unknown: 'Not visible'
	};

	function readGpu(): { webgl2: boolean; renderer: string } {
		try {
			const gl = document.createElement('canvas').getContext('webgl2');
			if (!gl) return { webgl2: false, renderer: '' };
			const ext = gl.getExtension('WEBGL_debug_renderer_info');
			const renderer = String(
				gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? ''
			);
			gl.getExtension('WEBGL_lose_context')?.loseContext();
			return { webgl2: true, renderer };
		} catch {
			return { webgl2: false, renderer: '' };
		}
	}

	/** "ANGLE (Apple, ANGLE Metal Renderer: Apple M2 Pro, Unspecified Version)" → "Apple M2 Pro". */
	function tidyRenderer(r: string): string {
		const inner = r.match(
			/ANGLE \((?:[^,]+), (.+?)(?: Direct3D.*| \(0x.*|, Unspecified.*)?\)$/
		)?.[1];
		return (inner ?? r).replace(/^ANGLE Metal Renderer: /, '').trim();
	}

	function detect(): Probe {
		const nav = navigator as Navigator & {
			deviceMemory?: number;
			userAgentData?: { platform?: string };
		};
		const plat = nav.userAgentData?.platform || nav.platform || nav.userAgent;
		const touchMac = /Mac/i.test(plat) && nav.maxTouchPoints > 1;
		const os: Probe['os'] = touchMac
			? 'other'
			: /Mac/i.test(plat)
				? 'mac'
				: /Win/i.test(plat)
					? 'windows'
					: 'other';

		const gpu = readGpu();
		const renderer = tidyRenderer(gpu.renderer);
		const checks: Check[] = [];

		if (os === 'mac') {
			const chip = renderer.match(/Apple (M\d+(?: (?:Pro|Max|Ultra))?)/)?.[1];
			const intel = /Intel|Radeon|NVIDIA|AMD/i.test(renderer);
			checks.push({
				label: 'Chip',
				value: chip ? `Apple ${chip}` : intel ? 'Intel Mac' : 'Hidden by this browser',
				grade: chip ? (/^M1$/.test(chip) ? 'min' : 'rec') : intel ? 'below' : 'unknown'
			});
		} else {
			checks.push({
				label: 'Graphics',
				value: !gpu.webgl2 ? 'No WebGL 2' : renderer || 'WebGL 2',
				grade: !gpu.webgl2
					? 'below'
					: /NVIDIA|GeForce|RTX|GTX|Radeon RX|Arc/i.test(renderer)
						? 'rec'
						: 'min'
			});
		}

		const threads = nav.hardwareConcurrency;
		checks.push({
			label: 'Processor threads',
			value: threads ? String(threads) : 'Hidden',
			grade: !threads ? 'unknown' : threads >= 12 ? 'rec' : threads >= 4 ? 'min' : 'below'
		});

		const mem = nav.deviceMemory;
		checks.push({
			label: 'Memory',
			// Chromium rounds down and caps at 8, so 8 only ever means "8 or more".
			value:
				mem === undefined
					? 'Your browser keeps this private'
					: mem >= 8
						? '8 GB or more'
						: `About ${mem} GB`,
			grade: mem === undefined ? 'unknown' : mem >= 8 ? 'ok' : 'below'
		});

		const w = screen.width;
		const h = screen.height;
		checks.push({
			label: 'Screen',
			value: `${w} × ${h}`,
			grade: w >= 1680 && h >= 1050 ? 'rec' : w >= 1440 && h >= 900 ? 'min' : 'below'
		});

		return { os, checks };
	}

	// Browser APIs only exist client side; read them once and point the sheet
	// at the machine the visitor is actually on.
	$effect(() => {
		const result = detect();
		probe = result;
		if (result.os !== 'other') platform = result.os;
	});
</script>

<div class="req">
	<SectionHead
		{index}
		label="Your computer"
		heading="What your computer needs."
		intro="PPF Engine does the geometry on your own machine, not in the cloud. Scans and patterns never leave it. Flattening is the heavy step: more memory and cores shorten the wait, they never change the pattern."
	/>

	<div class="req__grid">
		<!-- data-md: agents get both platforms as tables instead (lib/seo/llms.ts). -->
		<div class="sheet" data-md="requirements">
			<fieldset class="switch" style:--at={platform === 'mac' ? 0 : 1}>
				<legend class="sr-only">Platform</legend>
				<span class="switch__thumb" aria-hidden="true"></span>
				{#each platforms as p (p.id)}
					<label class="switch__opt">
						<input type="radio" name="platform" value={p.id} bind:group={platform} />
						<span class="switch__name">{p.name}</span>
						<span class="switch__arch">{p.arch}</span>
					</label>
				{/each}
			</fieldset>

			<p class="status" data-tested={spec.tested}>
				<span class="status__dot" aria-hidden="true"></span>{spec.status}
			</p>

			{#key platform}
				<table class="table">
					<caption class="sr-only">{spec.name} requirements, minimum and recommended</caption>
					<thead>
						<tr>
							<th scope="col"><span class="sr-only">Component</span></th>
							<th scope="col" class="table__min">Minimum</th>
							<th scope="col" class="table__rec"><span>Recommended</span></th>
						</tr>
					</thead>
					<tbody>
						{#each spec.rows as row, i (row.label)}
							<tr style:--i={i}>
								<th scope="row">{row.label}</th>
								<td class="table__min">{row.min}</td>
								<td class="table__rec">
									{row.rec}
									{#if row.note}<small>{row.note}</small>{/if}
								</td>
							</tr>
						{/each}
					</tbody>
				</table>
			{/key}

			<div class="foot">
				<p class="foot__label">Will not run on</p>
				<ul class="foot__list">
					{#each unsupported as u (u)}<li>{u}</li>{/each}
				</ul>
			</div>
		</div>

		<aside
			class="probe"
			aria-label="This computer"
			data-grade={verdict?.grade ?? 'wait'}
			data-md="skip"
		>
			<div class="probe__scan" aria-hidden="true"></div>
			<p class="probe__label">
				<span class="probe__led" aria-hidden="true"></span>
				This computer
			</p>

			<div aria-live="polite">
				{#if probe && verdict}
					<p class="probe__os">
						{probe.os === 'mac' ? 'Mac' : probe.os === 'windows' ? 'Windows PC' : 'Phone or tablet'}
					</p>
					<p class="probe__verdict">{verdict.text}</p>
					{#if probe.os !== 'other'}
						<ul class="probe__rows">
							{#each probe.checks as c, i (c.label)}
								<li style:--i={i}>
									<span class="probe__k">{c.label}</span>
									<span class="probe__v">{c.value}</span>
									<span class="pill" data-grade={c.grade}>{gradeText[c.grade]}</span>
								</li>
							{/each}
						</ul>
					{/if}
				{:else}
					<p class="probe__os probe__os--wait">Reading this machine…</p>
				{/if}
			</div>

			<p class="probe__small">
				A quick read of what your browser shares, not a guarantee. Browsers hide free disk space and
				cap memory at 8 GB. Nothing is sent anywhere.
			</p>
		</aside>
	</div>

	<p class="footnote">
		<span>Our studio</span>{studioMachine}. These are our targets until delivery: we confirm them on
		clean machines first, and any change shows up here.
	</p>
</div>

<style>
	.req {
		--ease: var(--ease-out);
	}
	.req__grid {
		display: grid;
		gap: clamp(2rem, 4vw, 3rem);
		margin-top: clamp(2.5rem, 5vw, 4rem);
		align-items: start;
	}
	@media (min-width: 1024px) {
		.req__grid {
			grid-template-columns: minmax(0, 7fr) minmax(0, 4fr);
		}
		.probe {
			position: sticky;
			top: 6rem;
		}
	}

	/* ── Platform switch ────────────────────────────────────────────────── */
	.switch {
		--at: 0;
		position: relative;
		display: grid;
		grid-template-columns: 1fr 1fr;
		max-width: 26rem;
		margin: 0;
		padding: 0.3125rem;
		border: 1px solid var(--color-app-700);
		border-radius: 999px;
		background: var(--color-app-900);
		min-width: 0;
	}
	.switch__thumb {
		position: absolute;
		inset: 0.3125rem auto 0.3125rem 0.3125rem;
		width: calc(50% - 0.3125rem);
		border-radius: 999px;
		background: var(--color-bone-100);
		box-shadow:
			0 0 0 1px rgb(255 255 255 / 0.4) inset,
			0 10px 30px -10px color-mix(in srgb, var(--color-cut-400) 45%, transparent);
		transform: translateX(calc(var(--at) * 100%));
		transition: transform var(--dur-slow) var(--ease);
	}
	.switch__opt {
		position: relative;
		display: grid;
		justify-items: center;
		gap: 0.125rem;
		padding: 0.625rem 1rem;
		border-radius: 999px;
		cursor: pointer;
		color: var(--color-bone-300);
		transition: color var(--dur-med) var(--ease);
	}
	.switch__opt input {
		position: absolute;
		inset: 0;
		opacity: 0;
		cursor: pointer;
	}
	.switch__opt:has(input:checked) {
		color: var(--color-app-950);
	}
	.switch__opt:has(input:focus-visible) {
		outline: 2px solid var(--color-cut-400);
		outline-offset: 3px;
	}
	.switch__name {
		font-weight: 600;
		font-size: 1.0625rem;
		letter-spacing: -0.01em;
	}
	.switch__arch {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		opacity: 0.7;
	}

	.status {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		margin-top: 1.5rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-300);
	}
	.status__dot {
		width: 0.5rem;
		height: 0.5rem;
		border-radius: 50%;
		background: var(--color-bone-500);
	}
	.status[data-tested='true'] {
		color: var(--color-cut-400);
	}
	.status[data-tested='true'] .status__dot {
		background: var(--color-cut-400);
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-cut-400) 22%, transparent);
	}

	/* ── Spec sheet ─────────────────────────────────────────────────────── */
	.table {
		width: 100%;
		margin-top: 1.25rem;
		border-collapse: collapse;
		color: var(--color-bone-300);
	}
	.table thead th {
		padding: 0 0 0.875rem;
		text-align: left;
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		font-weight: 400;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.table thead .table__rec span {
		display: inline-block;
		padding: 0.25rem 0.625rem;
		border-radius: 999px;
		background: var(--color-cut-400);
		color: var(--color-app-950);
	}
	.table tbody tr {
		border-top: 1px solid var(--color-app-700);
		animation: row-in 520ms var(--ease) both;
		animation-delay: calc(var(--i) * 45ms);
	}
	.table tbody tr:last-child {
		border-bottom: 1px solid var(--color-app-700);
	}
	.table tbody th,
	.table td {
		padding: 1.125rem 1.5rem 1.125rem 0;
		vertical-align: top;
		text-align: left;
	}
	.table tbody th {
		width: 22%;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		font-weight: 400;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
		padding-top: 1.3rem;
	}
	.table__min {
		width: 36%;
	}
	td.table__rec {
		position: relative;
		padding-left: 1rem;
		color: var(--color-bone-100);
		font-weight: 500;
		background: linear-gradient(
			90deg,
			color-mix(in srgb, var(--color-cut-400) 9%, transparent),
			transparent 85%
		);
		box-shadow: inset 2px 0 0 var(--color-cut-400);
	}
	.table thead .table__rec {
		padding-left: 1rem;
	}
	td.table__rec small {
		display: block;
		margin-top: 0.375rem;
		font-size: 0.875rem;
		font-weight: 400;
		line-height: 1.45;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	@keyframes row-in {
		from {
			opacity: 0;
			transform: translateY(8px);
		}
	}

	/* Narrow screens: each row becomes a card, the label on top. */
	@media (max-width: 640px) {
		.table thead th:first-child {
			display: none;
		}
		.table thead tr {
			display: grid;
			grid-template-columns: 1fr 1fr;
		}
		.table tbody tr {
			display: grid;
			grid-template-columns: 1fr 1fr;
			padding-block: 1rem;
		}
		.table tbody th {
			grid-column: 1 / -1;
			width: auto;
			padding: 0 0 0.5rem;
		}
		.table td {
			width: auto;
			padding: 0.5rem 0.75rem 0.5rem 0;
		}
		td.table__rec {
			padding-left: 0.75rem;
		}
	}

	.foot {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.75rem 1rem;
		margin-top: 1.75rem;
	}
	.foot__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.foot__list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		list-style: none;
	}
	.foot__list li {
		padding: 0.3125rem 0.75rem;
		border: 1px dashed var(--color-app-500);
		border-radius: 999px;
		font-size: 0.875rem;
		color: var(--color-bone-500);
		text-decoration: line-through;
		text-decoration-color: color-mix(in srgb, var(--color-signal-red) 70%, transparent);
	}

	/* ── Live readout ───────────────────────────────────────────────────── */
	.probe {
		--tone: var(--color-bone-500);
		position: relative;
		overflow: hidden;
		padding: clamp(1.5rem, 3vw, 2rem);
		border: 1px solid var(--color-app-700);
		border-radius: var(--radius-win);
		background:
			radial-gradient(
				120% 70% at 100% 0%,
				color-mix(in srgb, var(--tone) 14%, transparent),
				transparent 60%
			),
			var(--color-app-900);
		box-shadow: 0 40px 80px -50px rgb(0 0 0 / 0.8);
		color: var(--color-bone-300);
		transition: background var(--dur-slow) var(--ease);
	}
	.probe[data-grade='rec'] {
		--tone: var(--color-cut-400);
	}
	.probe[data-grade='min'] {
		--tone: var(--color-bone-100);
	}
	.probe[data-grade='below'] {
		--tone: var(--color-signal-red);
	}
	.probe__scan {
		position: absolute;
		inset: 0;
		pointer-events: none;
		background: linear-gradient(
			180deg,
			transparent,
			color-mix(in srgb, var(--color-cut-400) 18%, transparent) 50%,
			transparent
		);
		height: 40%;
		transform: translateY(-100%);
		animation: scan 1.6s var(--ease-inout) 1 both;
	}
	@keyframes scan {
		to {
			transform: translateY(260%);
		}
	}
	.probe__label {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.probe__led {
		width: 0.5rem;
		height: 0.5rem;
		border-radius: 50%;
		background: var(--tone);
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--tone) 22%, transparent);
		animation: led 2.4s ease-in-out infinite;
	}
	@keyframes led {
		50% {
			box-shadow: 0 0 0 7px color-mix(in srgb, var(--tone) 6%, transparent);
		}
	}
	.probe__os {
		margin-top: 1.25rem;
		font-size: clamp(1.75rem, 3vw, 2.25rem);
		font-weight: 600;
		line-height: 1.05;
		letter-spacing: -0.03em;
		color: var(--tone);
	}
	.probe[data-grade='wait'] .probe__os,
	.probe[data-grade='min'] .probe__os {
		color: var(--color-bone-100);
	}
	.probe__os--wait {
		opacity: 0.6;
	}
	.probe__verdict {
		margin-top: 0.625rem;
		color: var(--color-bone-100);
		text-wrap: pretty;
	}
	.probe__rows {
		display: grid;
		list-style: none;
		margin-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	.probe__rows li {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		grid-template-areas: 'k pill' 'v v';
		gap: 0.25rem 0.75rem;
		padding-block: 0.875rem;
		border-bottom: 1px solid var(--color-app-700);
		animation: row-in 480ms var(--ease) both;
		animation-delay: calc(300ms + var(--i) * 90ms);
	}
	.probe__k {
		grid-area: k;
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.probe__v {
		grid-area: v;
		color: var(--color-bone-100);
		overflow-wrap: anywhere;
	}
	.pill {
		grid-area: pill;
		align-self: start;
		padding: 0.1875rem 0.5rem;
		border-radius: 999px;
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		border: 1px solid var(--color-app-500);
		color: var(--color-bone-500);
	}
	.pill[data-grade='rec'] {
		border-color: var(--color-cut-400);
		background: var(--color-cut-400);
		color: var(--color-app-950);
	}
	.pill[data-grade='min'],
	.pill[data-grade='ok'] {
		border-color: var(--color-bone-300);
		color: var(--color-bone-100);
	}
	.pill[data-grade='below'] {
		border-color: var(--color-signal-red);
		color: #ff8f92;
	}
	.probe__small {
		margin-top: 1.25rem;
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--color-bone-500);
	}

	.footnote {
		max-width: 70ch;
		margin-top: clamp(2rem, 4vw, 3rem);
		color: var(--color-bone-500);
	}
	.footnote span {
		margin-right: 0.75rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-cut-400);
	}

	@media (prefers-reduced-motion: reduce) {
		.switch__thumb,
		.table tbody tr,
		.probe__rows li,
		.probe__scan,
		.probe__led {
			animation: none;
			transition: none;
		}
		.probe__scan {
			display: none;
		}
	}
</style>
